let audioContext: AudioContext | null = null
let noiseBuffer: AudioBuffer | null = null

function getAudioContext(): AudioContext {
    if (!audioContext) {
        audioContext = new AudioContext()
    }
    if (audioContext.state === 'suspended') {
        audioContext.resume()
    }
    return audioContext
}

/** Pre-generate a short white noise buffer (reused for all clicks) */
function getNoiseBuffer(): AudioBuffer {
    if (noiseBuffer) return noiseBuffer
    const ctx = getAudioContext()
    const sampleRate = ctx.sampleRate
    const length = Math.ceil(sampleRate * 0.05) // 50ms of noise
    noiseBuffer = ctx.createBuffer(1, length, sampleRate)
    const data = noiseBuffer.getChannelData(0)
    for (let i = 0; i < length; i++) {
        data[i] = Math.random() * 2 - 1
    }
    return noiseBuffer
}

export function initAudioOnInteraction(): void {
    getAudioContext()
    getNoiseBuffer()
}

/**
 * Primary click — the mechanical "snap" of a pull-chain switch engaging.
 * Built from a shaped noise burst, not oscillators, so it sounds physical.
 */
export function playPrimaryClick(): void {
    const ctx = getAudioContext()
    const now = ctx.currentTime

    // --- Layer 1: sharp noise transient (the "tick") ---
    const noiseSrc = ctx.createBufferSource()
    noiseSrc.buffer = getNoiseBuffer()

    // Highpass to keep it crisp and clicky, not boomy
    const highpass = ctx.createBiquadFilter()
    highpass.type = 'highpass'
    highpass.frequency.setValueAtTime(1800, now)
    highpass.Q.setValueAtTime(0.7, now)

    // Bandpass resonance for a slight metallic ring
    const resonance = ctx.createBiquadFilter()
    resonance.type = 'bandpass'
    resonance.frequency.setValueAtTime(4000, now)
    resonance.Q.setValueAtTime(8, now)

    // Very fast envelope: instant attack, ~15ms decay
    const noiseGain = ctx.createGain()
    noiseGain.gain.setValueAtTime(0.6, now)
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.015)

    noiseSrc.connect(highpass)
    highpass.connect(resonance)
    resonance.connect(noiseGain)

    // --- Layer 2: low thud for mechanical body ---
    const thudSrc = ctx.createBufferSource()
    thudSrc.buffer = getNoiseBuffer()

    const lowpass = ctx.createBiquadFilter()
    lowpass.type = 'lowpass'
    lowpass.frequency.setValueAtTime(500, now)
    lowpass.Q.setValueAtTime(1, now)

    const thudGain = ctx.createGain()
    thudGain.gain.setValueAtTime(0.25, now)
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.025)

    thudSrc.connect(lowpass)
    lowpass.connect(thudGain)

    // --- Mix both layers ---
    const masterGain = ctx.createGain()
    masterGain.gain.setValueAtTime(0.8, now)

    noiseGain.connect(masterGain)
    thudGain.connect(masterGain)
    masterGain.connect(ctx.destination)

    noiseSrc.start(now)
    thudSrc.start(now)
    noiseSrc.stop(now + 0.05)
    thudSrc.stop(now + 0.05)
}

/**
 * Secondary click — the softer settle click when the switch mechanism returns.
 * Same character as primary but quieter, slightly duller.
 */
export function playSecondaryClick(): void {
    const ctx = getAudioContext()
    const now = ctx.currentTime

    const noiseSrc = ctx.createBufferSource()
    noiseSrc.buffer = getNoiseBuffer()

    // Slightly lower highpass = duller click
    const highpass = ctx.createBiquadFilter()
    highpass.type = 'highpass'
    highpass.frequency.setValueAtTime(1200, now)
    highpass.Q.setValueAtTime(0.5, now)

    const resonance = ctx.createBiquadFilter()
    resonance.type = 'bandpass'
    resonance.frequency.setValueAtTime(3000, now)
    resonance.Q.setValueAtTime(5, now)

    // Even shorter, quieter
    const noiseGain = ctx.createGain()
    noiseGain.gain.setValueAtTime(0.3, now)
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.012)

    // Subtle low thud
    const thudSrc = ctx.createBufferSource()
    thudSrc.buffer = getNoiseBuffer()

    const lowpass = ctx.createBiquadFilter()
    lowpass.type = 'lowpass'
    lowpass.frequency.setValueAtTime(400, now)
    lowpass.Q.setValueAtTime(0.8, now)

    const thudGain = ctx.createGain()
    thudGain.gain.setValueAtTime(0.12, now)
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.018)

    noiseSrc.connect(highpass)
    highpass.connect(resonance)
    resonance.connect(noiseGain)
    thudSrc.connect(lowpass)
    lowpass.connect(thudGain)

    const masterGain = ctx.createGain()
    masterGain.gain.setValueAtTime(0.5, now)

    noiseGain.connect(masterGain)
    thudGain.connect(masterGain)
    masterGain.connect(ctx.destination)

    noiseSrc.start(now)
    thudSrc.start(now)
    noiseSrc.stop(now + 0.04)
    thudSrc.stop(now + 0.04)
}
